import type { StoreItem } from '../types/get-store-items';
import type { AddonCategory } from '../types/addons';
import { getEffectivePrice } from './promotion';

/**
 * Calcula o preço mínimo do item considerando addons obrigatórios.
 * Para cada categoria obrigatória, soma o preço do addon mais barato.
 *
 * A base usada é o preço efetivo do produto (preço de venda promocional quando
 * há promoção válida; caso contrário, o preço cheio) — garantindo que o valor
 * exibido no cardápio já reflita a promoção.
 *
 * @returns {
 *   minPrice: preço mínimo efetivo (base efetiva + piso de adicionais),
 *   addonFloor: piso de adicionais obrigatórios (parte independente do preço base),
 *   hasRequiredAddons: se há categorias obrigatórias,
 * }
 */
function calculateComboGroupsFloor(item: StoreItem): number {
  if (!Array.isArray(item.combo_groups)) return 0;
  return item.combo_groups.reduce((sum, group) => {
    const min = Math.max(0, Math.floor(Number(group.min_choices) || 0));
    const prices = (group.options || []).map(option => Number(option.additional_price) || 0);
    if (min === 0 || prices.length === 0) return sum;
    return sum + min * Math.min(...prices);
  }, 0);
}

export function calculateMinPrice(item: StoreItem): {
  minPrice: number;
  addonFloor: number;
  hasRequiredAddons: boolean;
} {
  const basePrice = getEffectivePrice(item);
  const addonCategories = item.addon_categories || [];

  // Filtra categorias obrigatórias (mandatory === 1)
  const requiredCategories = addonCategories.filter(
    (category: AddonCategory) => category.mandatory === 1 && category.addons?.length > 0
  );

  // Combo de escolha (PRAT-79): o preço base pode ser 0,00 e a cobrança vir das
  // etapas obrigatórias. O piso é o mínimo de escolhas × o produto mais barato.
  const comboFloor = calculateComboGroupsFloor(item);

  if (requiredCategories.length === 0) {
    return {
      minPrice: basePrice + comboFloor,
      addonFloor: comboFloor,
      hasRequiredAddons: comboFloor > 0,
    };
  }

  // Para cada categoria obrigatória, encontra o addon mais barato
  let addonFloor = comboFloor;
  for (const category of requiredCategories) {
    const prices = category.addons.map(addon => Number(addon.price) || 0);
    const minAddonPrice = Math.min(...prices);
    addonFloor += minAddonPrice;
  }

  return {
    minPrice: basePrice + addonFloor,
    addonFloor,
    hasRequiredAddons: true,
  };
}
